<template>
	<!-- строка-черновик в списке задач контекста: выглядит как context-task, создаёт задачу без роутинга -->
	<div v-if="row && !isReadonly" class="context-task context-task--new new-task-input-row">
		<div class="context-task__item" :style="rowItemStyle">
			<task-color :item="draft" local @change="onColor"/>

			<div class="context-task__slide">
				<div class="context-task__slide-content">
					<div v-if="isMD" class="context-task__btn-opener --mt d-none d-md-inline"></div>

					<div class="context-task__index grey--text caption">{{ goalTrack ? "" : index }}</div>

					<div class="context-task__btn-prio" :class="goalTrack ? '--track' : '--mt'">
						<!-- отрезок линии процесса между последним шагом и целью -->
						<btn-prio :item="draft" small is-subcontent :handler="onPrio"
								  :exclude-prios="excludePrios" no-sub-buttons
								  :track="goalTrack ? 'step' : null"
								  :track-state="goalReached ? 'passed' : 'upcoming'"/>
					</div>

					<div class="context-task__line new-task-input-row__line">
						<div class="context-task__name new-task-input-row__name">
							<input ref="input"
								   v-model="newTitle"
								   maxlength="255"
								   aria-label="Название новой задачи"
								   :placeholder="placeholder || 'новая задача...'"
								   class="new-task-input__input"
								   @keydown.enter="onEnter">
						</div>
					</div>

					<div v-if="isMD" class="context-task__tools --mt">
						<assignee :item="draft" small :class="{'--dimmed': !!draft.userId}" :handler="onAssignee"/>
						<!-- место под кнопку меню задачи (как в обычных строках), чтобы исполнитель стоял на одной вертикали -->
						<div class="new-task-input-row__menu-spacer"></div>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div v-else-if="!row && !isReadonly" class="new-task-input" :style="componentStyle">
<!--		<v-icon small color="grey" class="new-task-input__icon">mdi-plus</v-icon>-->
		<input ref="input"
			   v-model="newTitle"
			   maxlength="255"
			   aria-label="Название новой подзадачи"
			   :placeholder="placeholder"
			   class="new-task-input__input"
			   @keydown.enter="onEnter">
	</div>
</template>

<script>
import base from "@/mixins/base";
import vp from "@/mixins/vp";
import TaskColor from "@/components/task/task-color";
import BtnPrio from "@/components/btns/btn-prio";
import Assignee from "@/components/elements/assignee";
import {hasProcessInChain, PRIOS} from "@/utils/task";

export default {
	name: "new-task-input",
	mixins: [base, vp],
	components: {TaskColor, BtnPrio, Assignee},
	props: {
		/** Режим строки в списке context-tasks (с индексом, приоритетом, исполнителем, цветом). */
		row: {
			type: Boolean,
			default: false,
		},
		/** Контекст, в котором создаются задачи (только для режима row). */
		contextId: {
			type: String,
			default: null,
		},
		/** Id группы - по ней меню исполнителя показывает пользователей группы (как у обычных задач). */
		groupId: {
			type: String,
			default: null,
		},
		/** Порядковый номер строки (только для режима row). */
		index: {
			type: Number,
			default: undefined,
		},
		/** Уровень вложенности для отступа (только для режима row). */
		level: {
			type: Number,
			default: 0,
		},
		parent: {
			type: Object,
		},
		/** Под строкой выводится цель процесса - рисуем сквозь строку отрезок линии процесса (только для режима row). */
		withGoalTrack: {
			type: Boolean,
			default: false,
		},
		placeholder: {
			type: String,
		},
		prepend: {
			type: Boolean,
			default: false,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		leftPadding: {
			type: Number,
			default: undefined,
		},
	},
	data() {
		return {
			newTitle: null,
			draft: this.emptyDraft(),
		};
	},
	computed: {
		isReadonly() {
			if (this.row) return this.readonly;
			return this.readonly || !this.hasFullAccessToTask(this.parent);
		},
		rowItemStyle() {
			if (!this.isMD) return null;
			let left = this.level * 26;
			//if (hasProcessInChain(this.parent, this.context.item)) left += 32;
			return {paddingLeft: left + "px"};
		},
		/** Пункты меню приоритета, не нужные при создании задачи: "Комментарий" и "Скрыть" */
		excludePrios() {
			return [PRIOS.COMMENT, PRIOS.HIDDEN];
		},
		/** Рисовать ли отрезок линии процесса в btn-prio */
		goalTrack() {
			return this.withGoalTrack && this.isMD;
		},
		/** Цель процесса достигнута (не осталось шагов к выполнению) - линия зелёная */
		goalReached() {
			return !this.parent?.firstAssignableChild;
		},
		componentStyle() {
			const s = [];
			if (this.isMD) {
				let left = this.leftPadding ?? 46;
				s.push({paddingLeft: left + "px"});
			}
			return s.length ? s : null;
		}
	},
	watch: {
		/** смена контекста - исполнитель по умолчанию определяется заново */
		"parent.id"() {
			this.draft.userId = this.parent?.isUser ? this.parent.id : undefined;
		},
		groupId(v) {
			this.draft.groupIds = v ? [v] : undefined;
		},
	},
	methods: {
		/** Черновик новой задачи: fake id и withFullAccess нужны компонентам prio/assignee. */
		emptyDraft() {
			return {id: "__new-task-draft", withFullAccess: true, isActive: true, groupIds: this.groupId ? [this.groupId] : undefined, prio: undefined, withPrepend: false, userId: this.parent?.isUser ? this.parent.id : undefined, color: null};
		},
		/**
		 * @param prioItem
		 * @param forceParams - параметры sub-кнопки пункта меню (например, withPrepend - создать первой среди задач того же приоритета)
		 */
		onPrio(prioItem, forceParams) {
			if (prioItem?.onSelect || typeof prioItem?.prio === "undefined") return;
			this.draft.prio = typeof forceParams?.prio !== "undefined" ? forceParams.prio : prioItem.prio;
			this.draft.withPrepend = !!forceParams?.withPrepend;
		},
		onAssignee(user) {
			this.draft.userId = user?.id || null;
		},
		onColor(css) {
			this.draft.color = css;
		},
		/**
		 * Отдаёт фокус полю ввода. Вызывается и самим компонентом после отправки,
		 * и родителем через ref - на случай, если этот экземпляр будет уничтожен
		 * (v-if/v-else) до завершения обработки.
		 */
		focus() {
			this.$refs.input?.focus();
		},
		async onEnter() {
			const title = this.newTitle?.trim();
			if (!title) return;
			// очищаем поле СРАЗУ, не дожидаясь ответа сервера - пользователь должен иметь
			// возможность сразу печатать и отправлять следующую подзадачу. Несколько
			// одновременных сабмитов безопасны: каждый вызов onEnter() работает со своей
			// локальной копией title и своим отдельным await createNewTask() - друг другу
			// они не мешают (порядок реального index в БД при этом не гарантирован - и это ок)
			this.newTitle = null;
			const params = {title};
			if (this.prepend) params._withFirstPlace = true;
			if (this.row) {
				if (typeof this.draft.prio !== "undefined") params.prio = this.draft.prio;
				if (this.draft.withPrepend) params._withFirstPlace = true;
				if (typeof this.draft.userId !== "undefined") params.userId = this.draft.userId;
				if (this.draft.color) params.color = this.draft.color;
				this.draft = this.emptyDraft();
				await this.createNewTask(this.contextId, params, null, true);
				await this.$nextTick();
				this.focus();
				return;
			}
			// сообщаем родителю ДО запроса - если после ответа сервера этот экземпляр
			// пересоздастся (например, hasSubtasks сменится с false на true), фокус
			// новому экземпляру сможет вернуть только родитель, по стабильному ref
			this.$emit("creating");
			await this.createNewTask(this.parent.id, params, null, true);
			await this.$nextTick();
			this.focus();
		},
	},
}
</script>

<style lang="scss">
.new-task-input-row {
	// повышенная специфичность, чтобы перебить .context-task__line независимо от порядка стилей
	.new-task-input-row__line {
		align-self: stretch;
		align-items: center;
		height: auto;
		min-height: 38px;
		// зазор до исполнителя
		padding-right: 1rem;
	}

	// поле ввода занимает всю строку до исполнителя
	.new-task-input-row__name {
		flex: 1;
		min-width: 0;
	}

	// заданный исполнитель приглушён, пока курсор не над строкой
	.assignee.--dimmed {
		opacity: $default-disabled-opacity;
	}

	&:hover .assignee.--dimmed {
		opacity: 1;
	}

	.new-task-input-row__menu-spacer {
		width: 24px;
		flex-shrink: 0;
	}
}

.new-task-input {
	display: flex;
	align-items: center;
	gap: 1rem;
	padding: 8px 8px 8px 46px;

	&:hover {
		background: map-get($grey, "lighten-5");
	}

	&__input {
		flex: 1;
		width: 100%;
		outline: none;

		&::placeholder {
			color: $color-placeholder;
		}
	}
}
</style>
